<div class="space-y-6">
  <app-page-header
    [backTo]="['/my-transactions']"
    [backLabel]="'transactions.retourAMesTransactions' | t"
  />

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      [loadingMessage]="'transactions.chargementDeLaTransaction' | t"
      (retry)="fetch()"
    />
  } @else if (tx(); as t) {
    <!-- Header card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="space-y-2">
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">{{ t.idFromClient }}</h1>
            <span
              class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              [class.bg-brand-50]="statusMeta[t.transactionStatus].palette === 'brand'"
              [class.text-brand-700]="statusMeta[t.transactionStatus].palette === 'brand'"
              [class.dark:bg-brand-500/15]="statusMeta[t.transactionStatus].palette === 'brand'"
              [class.dark:text-brand-400]="statusMeta[t.transactionStatus].palette === 'brand'"
              [class.bg-blue-light-50]="statusMeta[t.transactionStatus].palette === 'blue-light'"
              [class.text-blue-light-700]="statusMeta[t.transactionStatus].palette === 'blue-light'"
              [class.dark:bg-blue-light-500/15]="statusMeta[t.transactionStatus].palette === 'blue-light'"
              [class.dark:text-blue-light-400]="statusMeta[t.transactionStatus].palette === 'blue-light'"
              [class.bg-warning-50]="statusMeta[t.transactionStatus].palette === 'warning'"
              [class.text-warning-700]="statusMeta[t.transactionStatus].palette === 'warning'"
              [class.dark:bg-warning-500/15]="statusMeta[t.transactionStatus].palette === 'warning'"
              [class.dark:text-warning-400]="statusMeta[t.transactionStatus].palette === 'warning'"
              [class.bg-gray-100]="statusMeta[t.transactionStatus].palette === 'gray'"
              [class.text-gray-700]="statusMeta[t.transactionStatus].palette === 'gray'"
              [class.dark:bg-gray-800]="statusMeta[t.transactionStatus].palette === 'gray'"
              [class.dark:text-gray-300]="statusMeta[t.transactionStatus].palette === 'gray'"
              [class.bg-error-50]="statusMeta[t.transactionStatus].palette === 'error'"
              [class.text-error-700]="statusMeta[t.transactionStatus].palette === 'error'"
              [class.dark:bg-error-500/15]="statusMeta[t.transactionStatus].palette === 'error'"
              [class.dark:text-error-400]="statusMeta[t.transactionStatus].palette === 'error'"
            >
              {{ statusMeta[t.transactionStatus].label | t }}
            </span>
            @if (metadataTypeLabel()) {
              <span class="inline-flex items-center gap-1 rounded-full bg-blue-light-50 px-2 py-0.5 text-xs font-medium text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400">
                {{ metadataTypeLabel() }}
              </span>
            }
            @if (t.callbackReceived) {
              <span class="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
                {{ 'transactions.callbackRecu' | t }}
              </span>
            }
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ sourceLabel[t.sourceService] || t.sourceService }}
            <span class="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
            @if (sourceLink(); as sl) {
              <a [routerLink]="sl.link" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                {{ sl.label }}
              </a>
            } @else if (t.sourceEntityType) {
              <span class="font-mono">{{ t.sourceEntityType }}#{{ t.sourceEntityId }}</span>
            } @else {
              <span class="font-mono">#{{ t.sourceEntityId }}</span>
            }
          </p>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ 'transactions.montant' | t }}</p>
          <p class="text-2xl font-bold text-gray-800 dark:text-white/90">
            {{ t.amount | number:'1.0-0' }} <span class="text-sm font-normal text-gray-400">{{ t.currency }}</span>
          </p>
          @if ((t.fees ?? 0) > 0) {
            <p class="text-xs text-gray-500 dark:text-gray-400">+ {{ t.fees | number:'1.0-0' }} {{ t.currency }} frais</p>
          }
        </div>
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-3">
      <!-- Identifiants -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Identifiants</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">ID client</dt>
            <dd class="text-right font-mono text-xs text-gray-800 dark:text-white/90 break-all">{{ t.idFromClient }}</dd>
          </div>
          @if (t.idFromGU) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">ID GUTouch</dt>
              <dd class="text-right font-mono text-xs text-gray-800 dark:text-white/90 break-all">{{ t.idFromGU }}</dd>
            </div>
          }
          @if (t.numTransaction) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">N° transaction</dt>
              <dd class="text-right font-mono text-xs text-gray-800 dark:text-white/90 break-all">{{ t.numTransaction }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- Bénéficiaire -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.beneficiaire' | t }}</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.nom' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ recipientLabel() }}</dd>
          </div>
          @if (t.recipientNumber) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.telephone' | t }}</dt>
              <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ t.recipientNumber }}</dd>
            </div>
          }
          @if (t.recipientEmail) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Email</dt>
              <dd class="text-right text-gray-800 dark:text-white/90 break-all">{{ t.recipientEmail }}</dd>
            </div>
          }
          @if (t.user || t.userId) {
            <div class="border-t border-gray-100 pt-3 dark:border-gray-800">
              <p class="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Utilisateur</p>
              <!-- noAutoLink : pas de route /users dans l'app owner (fork admin). -->
              <app-user-cell [user]="t.user" [fallbackId]="t.userId" size="md" [noAutoLink]="true" />
            </div>
          }
        </dl>
      </section>

      <!-- Méthode -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.methode' | t }}</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.moyenDePaiement' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ t.paymentMethod?.name || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Code service</dt>
            <dd class="text-right font-mono text-xs text-gray-800 dark:text-white/90 break-all">{{ t.serviceCode }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Devise</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ t.currency }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Métadonnées métier (type-aware card) -->
<!--    @if (metadata(); as m) {-->
<!--      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">-->
<!--        <div class="flex items-center justify-between">-->
<!--          <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.contexteMetier' | t }}</h2>-->
<!--          @if (metadataTypeLabel()) {-->
<!--            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">-->
<!--              {{ metadataTypeLabel() }}-->
<!--            </span>-->
<!--          }-->
<!--        </div>-->
<!--        <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">-->
<!--          @switch (m.type) {-->
<!--            @case ('RENTAL') {-->
<!--              @if (metaField('vehicle')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.vehicule' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('vehicle') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('immatriculation')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.immatriculation' | t }}</dt><dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ metaField('immatriculation') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('startDate')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.debut' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('startDate') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('endDate')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.fin' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('endDate') }}</dd></div>-->
<!--              }-->
<!--              @if (metadataInvoiceNumber(); as inv) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.facture' | t }}</dt><dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ inv }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @case ('RIDE') {-->
<!--              @if (metaField('vehicle')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.vehicule' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('vehicle') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('immatriculation')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.immatriculation' | t }}</dt><dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ metaField('immatriculation') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('startLocation')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.depart' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('startLocation') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('endLocation')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.arrivee' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('endLocation') }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @case ('UV_PURCHASE') {-->
<!--              @if (metaField('uvAmount') != null) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.uvAchetees' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('uvAmount') }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @case ('WITHDRAWAL') {-->
<!--              @if (metaField('source')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">Source</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('source') }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @case ('WALLET_TOPUP') {-->
<!--              @if (metaField('ownerType')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.typeDeWallet' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('ownerType') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('ownerRef') != null) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">Acteur</dt><dd class="text-right text-gray-800 dark:text-white/90">#{{ metaField('ownerRef') }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @case ('WALLET_WITHDRAWAL') {-->
<!--              @if (metaField('ownerType')) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.typeDeWallet' | t }}</dt><dd class="text-right text-gray-800 dark:text-white/90">{{ metaField('ownerType') }}</dd></div>-->
<!--              }-->
<!--              @if (metaField('ownerRef') != null) {-->
<!--                <div class="flex justify-between gap-4"><dt class="text-gray-500 dark:text-gray-400">Acteur</dt><dd class="text-right text-gray-800 dark:text-white/90">#{{ metaField('ownerRef') }}</dd></div>-->
<!--              }-->
<!--            }-->
<!--            @default {-->
<!--              <p class="col-span-2 text-sm text-gray-500 dark:text-gray-400">{{ 'transactions.typeNonReconnu' | t }} <span class="font-mono">{{ m.type }}</span>{{ 'transactions.detailsDansLeJson' | t }}</p>-->
<!--            }-->
<!--          }-->
<!--        </dl>-->
<!--      </section>-->
<!--    }-->

    <!-- Liens de redirection / payment UI -->
    @if (t.paymentUrl || t.deepLink || t.qrCode) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.canauxDePaiement' | t }}</h2>
        <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          @if (t.paymentUrl) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.urlDePaiement' | t }}</dt>
              <dd class="text-right">
                <a [href]="t.paymentUrl" target="_blank" rel="noopener" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">Ouvrir</a>
              </dd>
            </div>
          }
          @if (t.deepLink) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Deep link</dt>
              <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{{ t.deepLink }}</dd>
            </div>
          }
          @if (t.qrCode) {
            <div class="flex justify-between gap-4 sm:col-span-2">
              <dt class="text-gray-500 dark:text-gray-400">QR code</dt>
              <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{{ t.qrCode.slice(0, 80) }}{{ t.qrCode.length > 80 ? '…' : '' }}</dd>
            </div>
          }
        </dl>
      </section>
    }

    <!-- Callback & dates -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.callbackTracabilite' | t }}</h2>
      <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.callbackRecu2' | t }}</dt>
          <dd class="text-right">
            @if (t.callbackReceived) {
              <span class="text-brand-700 dark:text-brand-400">{{ 'transactions.oui' | t }}</span>
            } @else {
              <span class="text-gray-400">{{ 'transactions.non' | t }}</span>
            }
          </dd>
        </div>
        @if (t.callbackReceivedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Date callback</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ t.callbackReceivedAt | ldate: 'dd/MM/yyyy HH:mm:ss' }}</dd>
          </div>
        }
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.creeeLe' | t }}</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ t.createdAt | ldate: 'dd/MM/yyyy HH:mm:ss' }}</dd>
        </div>
        @if (t.updatedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'transactions.miseAJour' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ t.updatedAt | ldate: 'dd/MM/yyyy HH:mm:ss' }}</dd>
          </div>
        }
      </dl>
    </section>

    <!-- Raw metadata JSON -->
<!--    @if (metadataPretty()) {-->
<!--      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">-->
<!--        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'transactions.metadonneesBrutesJson' | t }}</h2>-->
<!--        <pre class="mt-4 overflow-x-auto rounded-lg bg-gray-50 p-4 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">{{ metadataPretty() }}</pre>-->
<!--      </section>-->
<!--    }-->
  }
</div>
